import { useState, useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth, useCompany } from '../../firebase/auth';
import { createBusinessForCompany } from '../../firebase/firestore';
import { uploadFile } from '../../firebase/storage';
import { Timestamp } from 'firebase/firestore';
import FileInput from '../../components/form/FileInput';
import CountryInput from '../../components/form/CountryInput';
import CustomModal from '../../components/CustomModal';
import EmailVerificationForm from '../verification/EmailVerificationForm';

import Stack from '@mui/joy/Stack';
import Input from '@mui/joy/Input';
import Button from '@mui/joy/Button';
import FormLabel from '@mui/joy/FormLabel';
import FormControl from '@mui/joy/FormControl';
import FormHelperText from '@mui/joy/FormHelperText';
import Typography from '@mui/joy/Typography';
import { useSnackbar } from '../../utils/SnackbarContext';

const buildValidationSchema = (level) => {
    const fieldSchemas = {
        email: Yup.string().email('Invalid email').required('Required'),
        businessName: Yup.string().required('Required'),
        incorporationNumber: Yup.string().required('Required'),
        taxNumber: Yup.string().required('Required'),
        address: Yup.string().required('Required'),
        country: Yup.string().required('Required'),
        fields: Yup.object().shape(level.fields.reduce((acc, field) => ({
            ...acc,
            [field.name]: Yup.string().required(`${field.name} is required`)
        }), {})),
        documents: Yup.object().shape(level.documents.reduce((acc, document) => ({
            ...acc,
            [document.name]: Yup.object().shape(document.files.reduce((fileAcc, file) => ({
                ...fileAcc,
                [file]: Yup.string().required(`${file} is required`)
            }), {}))
        }), {}),
        )
    };

    return Yup.object().shape(fieldSchemas);
};

export default function BusinessCreateForm({ level, onComplete }) {
    const { authUser } = useAuth();
    const { authCompany } = useCompany();
    const showSnackbar = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [files, setFiles] = useState({});
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [isEmailVerified, setIsEmailVerified] = useState(false);

    const validationSchema = useMemo(() => buildValidationSchema(level), [level]);

    const formik = useFormik({
        validateOnBlur: false,
        validateOnChange: false,
        initialValues: {
            email: '',
            businessName: '',
            incorporationNumber: '',
            taxNumber: '',
            address: '',
            country: '',
            emailVerified: false,  // Initial state
            fields: level.fields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {}),
            documents: level.documents.reduce((acc, document) => ({
                ...acc,
                [document.name]: document.files.reduce((fileAcc, file) => ({ ...fileAcc, [file]: '' }), {})
            }), {}), // Store file names
        },
        validationSchema,
        onSubmit: (values) => {
            if (authCompany?.subscription?.limits?.businesses <= 0) {
                setLoading(false);
                showSnackbar("You have reached the limit of KYB verifications allowed in your plan", "danger");
                return;
            }
            if (level.verifications && level.verifications.includes("email") && !isEmailVerified) {
                setShowEmailModal(true);
            } else {
                handleCreate(values);
            }
        },
    });

    const handleFile = (event) => {
        const name = event.target.name;
        const file = event.target.files[0];
        const fileName = event.target.files[0].name;
        const documentName = name.split('.')[1];
        const fileKey = name.split('.')[2];

        setFiles({
            ...files,
            [documentName]: {
                ...files[documentName],
                [fileKey]: file,
            }
        });

        formik.setFieldValue(name, fileName);
    };

    const handleEmailVerified = () => {
        setIsEmailVerified(true);  // Update state
        setShowEmailModal(false);
        formik.setFieldValue('emailVerified', true);  // Update the formik value
        formik.handleSubmit();
    };

    const handleCreate = async (business) => {
        setLoading(true);

        // Prepare business fields
        if (business.fields) {
            business.fields = Object.entries(business.fields).map(([name, value]) => ({ name, value }));
        } else {
            business.fields = [];
        }
        business.createdBy = authUser.uid;
        business.createdByName = authUser.displayName;

        let uploadedFiles = [];

        // Upload files and collect URLs
        for (const [documentName, fileGroup] of Object.entries(files)) {
            for (const [fileKey, file] of Object.entries(fileGroup)) {
                const fileBucket = `companies/${authUser.company.uid}/businesses/${business.incorporationNumber}`;
                const fileName = documentName === fileKey ? `${documentName}` : `${documentName}_${fileKey}`;
                const fileBucketPath = await uploadFile(file, fileBucket, fileName);
                if (!fileBucketPath) {
                    showSnackbar("Failed to upload file", "error");
                    setLoading(false);
                    return;
                }
                uploadedFiles.push({ name: documentName, file: fileKey, url: fileBucketPath });
            }
        }

        // Build business documents structure
        const businessDocuments = uploadedFiles.reduce((acc, { name, file, url }) => {
            const existingDoc = acc.find(doc => doc.name === name);
            const fileObj = { name: file, url: url };

            if (existingDoc) {
                existingDoc.files.push(fileObj);
            } else {
                acc.push({ name, files: [fileObj] });
            }

            return acc;
        }, []);

        // Prepare final business object with documents and status
        const newBusiness = {
            ...business,
            documents: businessDocuments,
            status: {
                status: 'pending',
                fields: 'pending',
                documents: businessDocuments.reduce((acc, doc) => ({ ...acc, [doc.name]: 'pending' }), {}),
                verifications: {
                    email: isEmailVerified
                }
            },
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            level: level
        };

        try {
            // Create business in Firestore with all data
            await createBusinessForCompany(authUser.company.uid, newBusiness, level);
            showSnackbar("Business created successfully", "success");
            onComplete();
        } catch (e) {
            console.error("Error creating business:", e);
            showSnackbar("Failed to create business", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <Stack spacing={2}>
                <Typography level="title-md">Company Information</Typography>
                <FormControl error={!!formik.errors.email} sx={{ flexGrow: 1 }}>
                    <FormLabel>Email</FormLabel>
                    <Input
                        type="text"
                        name="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                    />
                    {formik.errors.email && (
                        <FormHelperText>{formik.errors.email}</FormHelperText>
                    )}
                </FormControl>
                <FormControl error={!!formik.errors.businessName} sx={{ flexGrow: 1 }}>
                    <FormLabel>Company Name</FormLabel>
                    <Input
                        type="text"
                        name="businessName"
                        value={formik.values.businessName}
                        onChange={formik.handleChange}
                    />
                    {formik.errors.businessName && (
                        <FormHelperText>{formik.errors.businessName}</FormHelperText>
                    )}
                </FormControl>
                <FormControl error={!!formik.errors.taxNumber} sx={{ flexGrow: 1 }}>
                    <FormLabel>Tax Number</FormLabel>
                    <Input
                        type="text"
                        name="taxNumber"
                        value={formik.values.taxNumber}
                        onChange={formik.handleChange}
                    />
                    {formik.errors.taxNumber && (
                        <FormHelperText>{formik.errors.taxNumber}</FormHelperText>
                    )}
                </FormControl>
                <FormControl error={!!formik.errors.incorporationNumber} sx={{ flexGrow: 1 }}>
                    <FormLabel>Incorporation Number</FormLabel>
                    <Input
                        type="text"
                        name="incorporationNumber"
                        value={formik.values.incorporationNumber}
                        onChange={formik.handleChange}
                    />
                    {formik.errors.incorporationNumber && (
                        <FormHelperText>{formik.errors.incorporationNumber}</FormHelperText>
                    )}
                </FormControl>
                <FormControl error={!!formik.errors.address} sx={{ flexGrow: 1 }}>
                    <FormLabel>Registered Address</FormLabel>
                    <Input
                        type="text"
                        name="address"
                        value={formik.values.address}
                        onChange={formik.handleChange}
                    />
                    {formik.errors.address && (
                        <FormHelperText>{formik.errors.address}</FormHelperText>
                    )}
                </FormControl>
                <FormControl error={!!formik.errors.country} sx={{ flexGrow: 1 }}>
                    <FormLabel>Country of Incorporation</FormLabel>
                    <CountryInput
                        name="country"
                        value={formik.values.country}
                        setFieldValue={formik.setFieldValue}
                        formik={formik}
                    />
                    {formik.errors.country && (
                        <FormHelperText>{formik.errors.country}</FormHelperText>
                    )}
                </FormControl>
                {level.fields && level.fields.length > 0 &&
                    <>
                        <Typography level="title-md">Additional Information</Typography>
                        <Stack direction="column" spacing={1}>
                            {level.fields.map((field) => (
                                <FormControl
                                    key={field.name}
                                    error={!!formik.errors.fields?.[field.name]}
                                    sx={{ flexGrow: 1 }}
                                >
                                    <FormLabel>{field.name}</FormLabel>
                                    <Input
                                        type="text"
                                        name={`fields.${field.name}`}
                                        value={formik.values.fields?.[field.name]}
                                        onChange={formik.handleChange}
                                    />
                                    {formik.errors.fields?.[field.name] && (
                                        <FormHelperText>{formik.errors.fields?.[field.name]}</FormHelperText>
                                    )}
                                </FormControl>
                            ))}
                        </Stack>
                    </>
                }
                <Typography level="title-md">Documents</Typography>
                <Stack spacing={2}>
                    {level.documents.map((document) =>
                        <FormControl
                            key={document.name}
                            error={!!formik.errors.documents?.[document.name]}
                        >
                            <FormLabel>{document.name}</FormLabel>
                            {
                            document.files.map((file) => 
                                <div key={file}>
                                    { file !== document.name && <FormLabel>{file}</FormLabel>}
                                    <FileInput
                                        name={`documents.${document.name}.${file}`}
                                        formik={formik}
                                        onChange={handleFile}
                                    />
                                    {formik.errors.documents?.[document.name]?.[file] && (
                                        <FormHelperText>{formik.errors.documents?.[document.name]?.[file]}</FormHelperText>
                                    )}
                                </div>
                            )}
                        </FormControl>
                    )}
                </Stack>
                <Button size="sm" variant="solid" color="primary" type="submit" loading={loading} disabled={loading}>Submit</Button>
            </Stack>
            <CustomModal open={showEmailModal}>
                <EmailVerificationForm email={formik.values.email} onComplete={handleEmailVerified} />
            </CustomModal>
        </form>
    );
}
